import React, { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, AlertCircle, ScrollText } from "lucide-react";
import AddressForm from "@/components/AddressForm";
import VehicleManager, { VEHICLE_LABELS, VEHICLE_ICON, SELF_DRIVEN } from "@/components/VehicleManager";

const PostTrip = ({ onCreated }) => {
  const { user } = useAuth();
  const vehicles = user?.vehicles || [];
  const [showVehicleSetup, setShowVehicleSetup] = useState(false);
  const [showAgreement, setShowAgreement] = useState(false);
  const [agreementChecked, setAgreementChecked] = useState(false);
  const [agreementAccepted, setAgreementAccepted] = useState(false);
  const [agreementVersion, setAgreementVersion] = useState("1.0");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/legal/my-acceptances");
        if (cancelled) return;
        const v = r.data.versions?.carrier_agreement || "1.0";
        setAgreementVersion(v);
        setAgreementAccepted(r.data.accepted?.carrier_agreement === v);
      } catch {
        /* ignore */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const [form, setForm] = useState({
    origin: "",
    destination: "",
    distance_km: 0,
    date: new Date(Date.now() + 86400000).toISOString().slice(0, 10),
    travel_mode: vehicles[0]?.type || "",
    vehicle_id: vehicles[0]?.id || "",
    capacity_size: "medium",
    notes: "",
    departure_time: "08:00",
    arrival_time: "18:00",
    origin_point: null,
    destination_point: null,
    duration_min: 0,
    boarding_point: "",
    alighting_point: "",
  });
  const [busy, setBusy] = useState(false);
  const [distanceState, setDistanceState] = useState({ loading: false, error: "" });

  useEffect(() => {
    if (!vehicles.find((v) => v.id === form.vehicle_id)) {
      setForm((f) => ({ ...f, travel_mode: vehicles[0]?.type || "", vehicle_id: vehicles[0]?.id || "" }));
    }
    // eslint-disable-next-line
  }, [vehicles.length]);

  const isTransit = !SELF_DRIVEN.has(form.travel_mode);

  useEffect(() => {
    if (form.origin_point && form.destination_point) {
      setDistanceState({ loading: true, error: "" });
      const p = form.origin_point; const d = form.destination_point;
      api.get(`/geo/route?from_lat=${p.lat}&from_lng=${p.lng}&to_lat=${d.lat}&to_lng=${d.lng}`)
        .then((r) => {
          setForm((f) => ({
            ...f,
            distance_km: r.data.distance_km,
            duration_min: r.data.duration_min,
            origin: `${f.origin_point?.area || f.origin_point?.city || ""}${f.origin_point?.city ? ", " + f.origin_point.city : ""}`,
            destination: `${f.destination_point?.area || f.destination_point?.city || ""}${f.destination_point?.city ? ", " + f.destination_point.city : ""}`,
          }));
          setDistanceState({ loading: false, error: "" });
        })
        .catch(() => setDistanceState({ loading: false, error: "Couldn't calculate distance — please check both addresses." }));
    }
    // eslint-disable-next-line
  }, [form.origin_point?.lat, form.origin_point?.lng, form.destination_point?.lat, form.destination_point?.lng]);

  const submit = async () => {
    if (!agreementAccepted) {
      setShowAgreement(true);
      return;
    }
    if (!form.travel_mode) return toast.error("Pick a vehicle for this trip");
    if (!form.origin_point || !form.destination_point) return toast.error("Search and select both origin and destination");
    if (distanceState.loading) return toast.error("Please wait for the distance to compute");
    if (!form.distance_km || form.distance_km <= 0) return toast.error("We couldn't calculate the driving distance for this route. Please re-select the endpoints or try again.");
    if (!form.departure_time) return toast.error("Departure time required");
    if (isTransit && !form.arrival_time) return toast.error("Arrival time required for train/bus");
    if (isTransit && (!form.boarding_point.trim() || !form.alighting_point.trim())) {
      return toast.error("Boarding & alighting terminals are required for bus/train trips");
    }
    setBusy(true);
    try {
      const dep = new Date(`${form.date}T${form.departure_time}:00`).toISOString();
      let arr = null;
      if (isTransit) {
        arr = new Date(`${form.date}T${form.arrival_time}:00`).toISOString();
        if (new Date(arr) <= new Date(dep)) arr = new Date(new Date(arr).getTime() + 86400000).toISOString();
      }
      const payload = {
        ...form,
        origin: form.origin || `${form.origin_point.area || form.origin_point.city}`,
        destination: form.destination || `${form.destination_point.area || form.destination_point.city}`,
        departure_time: dep,
        arrival_time: arr,
      };
      const r = await api.post("/trips", payload);
      toast.success("Trip published");
      onCreated?.(r.data);
      setForm({ ...form, origin_point: null, destination_point: null, origin: "", destination: "", notes: "", distance_km: 0 });
    } catch (e) { toast.error(apiError(e, "Failed")); }
    setBusy(false);
  };

  const acceptAgreement = async () => {
    try {
      await api.post("/legal/accept", { doc: "carrier_agreement", version: agreementVersion });
      setAgreementAccepted(true);
      setShowAgreement(false);
      toast.success("Carrier Agreement accepted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't record acceptance");
    }
  };

  if (vehicles.length === 0) {
    return (
      <div className="rounded-3xl bg-white border border-slate-200 p-6 shadow-sm" data-testid="vehicle-required-card">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-100 grid place-items-center shrink-0">
            <AlertCircle className="w-5 h-5 text-amber-700"/>
          </div>
          <div>
            <div className="font-display text-lg font-bold">Add a vehicle to post a trip</div>
            <p className="text-sm text-slate-500 mt-1">Every carrier must list at least one way they travel — self-driven (bike/car/SUV) or a pre-booked ticket (bus/train). This determines pricing and which parcels you can carry.</p>
          </div>
        </div>
        <div className="mt-5 border-t border-slate-100 pt-5">
          <VehicleManager />
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-3xl bg-white border border-slate-200 p-6 shadow-sm">
      <div className="flex items-center gap-2 mb-5">
        <div className="w-9 h-9 rounded-xl bg-blue-100 grid place-items-center"><ScrollText className="w-4 h-4 text-blue-700"/></div>
        <div>
          <div className="font-display text-lg font-bold">Publish an upcoming trip</div>
          <div className="text-xs text-slate-500">Get matched with senders on your route</div>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4 mb-4">
        <AddressForm label="Origin" value={form.origin_point} onChange={(pt) => setForm(f => ({ ...f, origin_point: pt }))} allowCurrentLocation allowSavedAddresses/>
        <AddressForm label="Destination" value={form.destination_point} onChange={(pt) => setForm(f => ({ ...f, destination_point: pt }))} allowSavedAddresses/>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-slate-500 uppercase tracking-wider">Date</label>
          <Input data-testid="trip-date" type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} className="rounded-xl mt-1"/>
        </div>
        <div>
          <label className="text-xs text-slate-500 uppercase tracking-wider">Departure time</label>
          <Input data-testid="trip-departure-time" type="time" value={form.departure_time} onChange={e => setForm({...form, departure_time: e.target.value})} className="rounded-xl mt-1"/>
        </div>
        {isTransit && (
          <div>
            <label className="text-xs text-slate-500 uppercase tracking-wider">Scheduled arrival</label>
            <Input data-testid="trip-arrival-time" type="time" value={form.arrival_time} onChange={e => setForm({...form, arrival_time: e.target.value})} className="rounded-xl mt-1"/>
            <div className="text-[11px] text-slate-400 mt-1">Overnight? We'll roll it to the next day automatically.</div>
          </div>
        )}
        {isTransit && (
          <div className="sm:col-span-2 rounded-2xl border-2 border-blue-100 bg-blue-50/50 p-4" data-testid="terminal-block">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-6 h-6 rounded-full bg-blue-100 grid place-items-center text-xs">🚉</div>
              <div className="text-sm font-semibold text-blue-900">Terminal-to-terminal handover</div>
            </div>
            <div className="text-[11px] text-blue-800 mb-3">Bus / train carriers hand parcels over at their boarding & alighting stations. The sender drops off and receiver collects there — unless they buy the optional Door add-on at booking.</div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] font-semibold text-blue-900 uppercase tracking-wider">Boarding terminal</label>
                <Input data-testid="trip-boarding-point" value={form.boarding_point} onChange={e => setForm({...form, boarding_point: e.target.value})} placeholder="e.g. Delhi ISBT Kashmere Gate" className="rounded-xl mt-1 bg-white"/>
              </div>
              <div>
                <label className="text-[11px] font-semibold text-blue-900 uppercase tracking-wider">Alighting terminal</label>
                <Input data-testid="trip-alighting-point" value={form.alighting_point} onChange={e => setForm({...form, alighting_point: e.target.value})} placeholder="e.g. Chandigarh ISBT Sector 43" className="rounded-xl mt-1 bg-white"/>
              </div>
            </div>
          </div>
        )}
        <div>
          <label className="text-xs text-slate-500 uppercase tracking-wider">Vehicle for this trip</label>
          <Select value={form.vehicle_id} onValueChange={(id) => setForm({...form, vehicle_id: id, travel_mode: vehicles.find((v) => v.id === id)?.type || ""})}>
            <SelectTrigger data-testid="trip-mode" className="rounded-xl mt-1"><SelectValue placeholder="Pick a vehicle"/></SelectTrigger>
            <SelectContent>
              {vehicles.filter(v => v.type !== "flight").map((v) => {
                const Icon = VEHICLE_ICON[v.type];
                return (
                  <SelectItem key={v.id} value={v.id} data-testid={`trip-vehicle-${v.id}`}>
                    <span className="inline-flex items-center gap-2">
                      <Icon className="w-3.5 h-3.5"/> {VEHICLE_LABELS[v.type]}
                      {SELF_DRIVEN.has(v.type) && v.reg_no ? <span className="text-slate-400 text-xs font-mono">· {v.reg_no}</span> : null}
                      {SELF_DRIVEN.has(v.type) && <span className={`text-[10px] font-semibold ${v.rc_status === "verified" ? "text-emerald-700" : "text-amber-700"}`}>{v.rc_status === "verified" ? "RC verified" : v.rc_status === "pending_review" ? "RC under review" : "RC not verified"}</span>}
                    </span>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
          <button type="button" onClick={() => setShowVehicleSetup(true)} className="text-[11px] text-amber-700 mt-1 hover:underline" data-testid="manage-vehicles-btn">Add or edit vehicles</button>
        </div>
        <div>
          <label className="text-xs text-slate-500 uppercase tracking-wider">Distance (auto)</label>
          <div className={`mt-1 min-h-10 rounded-xl border flex items-center px-3 font-mono text-sm ${distanceState.error ? "bg-rose-50 border-rose-200 text-rose-800" : "bg-slate-50 border-slate-200"}`} data-testid="trip-distance-display">
            {distanceState.loading
              ? <span className="text-slate-500 inline-flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin"/> Calculating driving distance…</span>
              : distanceState.error
                ? <span className="text-rose-700">{distanceState.error}</span>
                : form.distance_km
                  ? <span>{form.distance_km} km{form.duration_min ? ` · ~${Math.round(form.duration_min)} min` : ""}</span>
                  : <span className="text-slate-400">Fill both addresses to see the distance</span>}
          </div>
        </div>
        <div>
          <label className="text-xs text-slate-500 uppercase tracking-wider">Max capacity</label>
          <Select value={form.capacity_size} onValueChange={(v) => setForm({...form, capacity_size: v})}>
            <SelectTrigger data-testid="trip-capacity" className="rounded-xl mt-1"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="envelope">Envelope</SelectItem>
              <SelectItem value="small">Small</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="large">Large / Suitcase</SelectItem>
              <SelectItem value="xl">Extra large</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="sm:col-span-2">
          <label className="text-xs text-slate-500 uppercase tracking-wider">Notes</label>
          <Textarea data-testid="trip-notes" value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} className="rounded-xl mt-1" placeholder="Any special notes for senders"/>
        </div>
      </div>
      <Button data-testid="publish-trip-btn" disabled={busy} onClick={submit} className="mt-6 w-full rounded-full bg-slate-900 hover:bg-slate-800">Publish trip</Button>

      <Dialog open={showVehicleSetup} onOpenChange={setShowVehicleSetup}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>My vehicles</DialogTitle>
            <DialogDescription>Add or remove vehicles you can carry parcels with.</DialogDescription>
          </DialogHeader>
          <VehicleManager />
        </DialogContent>
      </Dialog>

      <Dialog open={showAgreement} onOpenChange={setShowAgreement}>
        <DialogContent className="max-w-md" data-testid="carrier-agreement-modal">
          <DialogHeader>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-8 h-8 rounded-lg bg-amber-100 grid place-items-center"><ScrollText className="w-4 h-4 text-amber-700"/></div>
              <DialogTitle>Accept the Carrier Agreement</DialogTitle>
            </div>
            <DialogDescription>
              Before publishing your first trip, please read and accept the Carrier Agreement (v{agreementVersion}). It sets out that you carry parcels along your existing trips as an independent individual.
            </DialogDescription>
          </DialogHeader>
          <Link to="/carrier-agreement" target="_blank" className="block rounded-xl border border-slate-200 bg-slate-50 hover:bg-slate-100 px-3 py-2 text-sm" data-testid="agreement-link">
            <div className="font-medium text-slate-900">Read the Carrier Agreement</div>
            <div className="text-[11px] text-slate-500">Opens in a new tab</div>
          </Link>
          <label className="flex items-start gap-2 pt-2 cursor-pointer select-none">
            <Checkbox
              checked={agreementChecked}
              onCheckedChange={(v) => setAgreementChecked(!!v)}
              data-testid="agreement-checkbox"
              className="mt-0.5"
            />
            <span className="text-xs text-slate-600 leading-snug">
              I confirm I am an independent individual carrying parcels along my existing trips, and I accept the Carrier Agreement.
            </span>
          </label>
          <Button data-testid="accept-agreement-btn" disabled={!agreementChecked} onClick={acceptAgreement} className="w-full rounded-full bg-slate-900 hover:bg-slate-800">
            Accept and continue
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PostTrip;
